
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { CheckIcon } from '@heroicons/react/solid';
import { GetStaticProps } from 'next';
import Head from 'next/head';
import useLocale from 'next-translate/useTranslation';
import Image from 'next/image';
import Link from 'next/link';
import React, { useState, useRef } from 'react';
import CallToAction from '@/components/CallToAction';
import FeaturedLogos from '@/components/FeaturedLogos';
import Footer from '@/components/layout/Footer';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import ButtonLink from '@/components/links/ButtonLink';
import LogosCloud from '@/components/LogosCloud';
import Seo from '@/components/Seo';
import Testimonials from '@/components/Testimonials';
import { ACTORS } from '@/constants/actors';
import translateReactTree from '@/utils/translateReactTree';
const SectionLabel = ({ children }: {
    children: React.ReactNode;
}) => (<span className='mb-4 inline-block text-xs font-bold uppercase tracking-[0.2em]' style={{ color: '#a78bfa' }}>
    {children}
  </span>);
const FEATURES = [
    {
        title: '60+ Pre-Built Avatars',
        description: 'Diverse professional presenters spanning different ages, ethnicities, and styles — ready to use instantly.',
    },
    {
        title: 'Custom Avatar Upload',
        description: 'Use your own portrait photo as an avatar. Our AI transforms any clear headshot into a lifelike talking character.',
    },
    {
        title: 'Realistic Lip-Sync',
        description: 'Advanced AI produces natural mouth movements perfectly synced to speech for believable results.',
    },
    {
        title: '40+ Languages',
        description: 'The same avatar speaks any language with native-quality pronunciation and natural intonation.',
    },
    {
        title: 'Voice Cloning',
        description: 'Match the avatar with your own cloned voice for a truly personalized digital presenter experience.',
    },
    {
        title: 'Auto Captions',
        description: 'Built-in subtitle generation ensures your avatar videos are accessible and engaging on every platform.',
    },
    {
        title: 'HD Quality Output',
        description: 'Professional 1080p video output that looks polished on websites, social media, and presentations.',
    },
    {
        title: 'Fast Generation',
        description: 'Avatar videos are generated in minutes, not hours. Go from script to finished video in under five minutes.',
    },
    {
        title: 'Commercial License',
        description: 'Full usage rights included with every plan. Use your avatar videos for business, marketing, and sales.',
    },
];
const STEPS = [
    {
        step: '01',
        title: 'Pick Your Avatar',
        description: 'Choose from 60+ pre-built avatars or upload your own portrait photo. Each avatar is designed for professional, lifelike results.',
    },
    {
        step: '02',
        title: 'Enter Your Script',
        description: 'Write or paste your text, then select a voice and language. Customize tone, speed, and accent to match your brand.',
    },
    {
        step: '03',
        title: 'Generate Video',
        description: 'Hit generate and our AI creates a talking avatar video with perfect lip-sync. Download in HD and share anywhere.',
    },
];
const USE_CASES = [
    {
        title: 'Content Creation at Scale',
        description: 'Produce dozens of talking-head videos per week without ever sitting in front of a camera. Perfect for YouTube, TikTok, and educational channels.',
    },
    {
        title: 'Corporate Communications',
        description: 'Deliver internal updates, training announcements, and executive messages with a consistent, professional avatar presenter.',
    },
    {
        title: 'E-Learning & Training',
        description: 'Build engaging course content with a virtual instructor who speaks clearly in any language your learners need.',
    },
    {
        title: 'Social Media Marketing',
        description: 'Create scroll-stopping video ads and organic posts featuring a polished digital spokesperson — no production crew required.',
    },
    {
        title: 'Customer Support Videos',
        description: 'Generate FAQ walkthroughs, onboarding tutorials, and product demos that feel personal and approachable.',
    },
    {
        title: 'Personal Branding',
        description: 'Maintain a consistent on-screen presence across platforms. Same look, same energy, every single video.',
    },
];
const FAQS = [
    {
        question: 'What is an AI talking avatar?',
        answer: 'An AI talking avatar is a digital character powered by artificial intelligence that can speak any text you provide with realistic lip movements, facial expressions, and natural gestures. It allows you to create professional talking-head videos without a camera, studio, or any recording equipment.',
    },
    {
        question: 'How do I create a talking AI avatar?',
        answer: 'Choose from our library of 60+ pre-built avatars or upload your own portrait photo. Then write your script, select a voice and language, and click generate. The AI handles lip-syncing, facial animation, and video rendering automatically. Most videos are ready in 2-5 minutes.',
    },
    {
        question: 'Can I use my own photo as an avatar?',
        answer: 'Yes! Upload any clear, front-facing portrait photo and our AI will transform it into a fully animated talking avatar. The photo should have good lighting, a neutral expression, and a clean background for the best results.',
    },
    {
        question: 'How many languages do AI avatars support?',
        answer: 'Our AI avatars support 40+ languages and accents, including English, Spanish, French, German, Chinese, Japanese, Korean, Arabic, Hindi, and many more. The same avatar can speak any supported language with native-quality pronunciation.',
    },
    {
        question: 'Are AI avatar videos good enough for professional use?',
        answer: 'Absolutely. Our AI generates HD 1080p videos with natural lip-sync and smooth facial movements. The output is suitable for corporate presentations, marketing campaigns, e-learning courses, social media content, and customer-facing communications.',
    },
    {
        question: 'How long does it take to create an avatar video?',
        answer: 'Most avatar videos are generated within 2-5 minutes depending on the length of your script. This is dramatically faster than traditional video production, which can take hours or days for filming, editing, and post-production.',
    },
];
const COMPARISON_ROWS = [
    { aspect: 'Camera needed', avatar: 'No', self: 'Yes' },
    { aspect: 'Makeup & styling', avatar: 'Never', self: 'Every time' },
    {
        aspect: 'Multiple languages',
        avatar: '40+ instantly',
        self: 'Learn or hire',
    },
    {
        aspect: 'Consistency',
        avatar: 'Perfect every time',
        self: 'Varies by day',
    },
    { aspect: 'Time to create', avatar: '2-5 minutes', self: 'Hours of setup' },
    { aspect: 'Editing needed', avatar: 'None', self: 'Extensive' },
    { aspect: 'Scale', avatar: 'Unlimited', self: 'Limited by energy' },
];
const RELATED_TOOLS = [
    {
        title: 'AI Talking Head Videos',
        description: 'Turn any portrait into a talking video with AI-powered lip sync and voice.',
        href: '/ai-video-generator/talking-head',
    },
    {
        title: 'AI Spokesperson Videos',
        description: 'Create professional spokesperson videos for marketing and corporate communications.',
        href: '/ai-video-generator/spokesperson',
    },
    {
        title: 'Make Image Talk',
        description: 'Upload a photo and bring it to life with AI-generated speech and animation.',
        href: '/ai-video-generator/make-image-talk',
    },
];
const IDENTITY_BENEFITS = [
    'Consistent appearance across all videos',
    'Scale content production without being on camera',
    'Maintain professional look 24/7',
    'Create content in any language with the same avatar',
];
const AVATAR_PILLS = [
    'Same Face',
    'Any Language',
    'Any Script',
    'Always Available',
];
export default function TalkingAvatarLanding() {
    const { t } = useLocale('ai-video-talking-avatar');
    const jsonLd = {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'WebApplication',
                name: 'Easy-Peasy.AI Talking Avatar Generator',
                url: 'https://easy-peasy.ai/ai-video-generator/talking-avatar',
                applicationCategory: 'MultimediaApplication',
                operatingSystem: 'Web',
                description: 'Create AI talking avatars in minutes. Choose from 60+ pre-built avatars or upload your own photo. 40+ languages, realistic lip-sync, no camera needed.',
                offers: {
                    '@type': 'Offer',
                    price: '0',
                    priceCurrency: 'USD',
                },
                aggregateRating: {
                    '@type': 'AggregateRating',
                    ratingValue: '4.8',
                    ratingCount: '2340',
                },
            },
            {
                '@type': 'FAQPage',
                mainEntity: FAQS.map((faq) => ({
                    '@type': 'Question',
                    name: faq.question,
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: faq.answer,
                    },
                })),
            },
            {
                '@type': 'BreadcrumbList',
                itemListElement: [
                    {
                        '@type': 'ListItem',
                        position: 1,
                        name: 'Home',
                        item: 'https://easy-peasy.ai',
                    },
                    {
                        '@type': 'ListItem',
                        position: 2,
                        name: 'AI Video Generator',
                        item: 'https://easy-peasy.ai/ai-video-generator',
                    },
                    {
                        '@type': 'ListItem',
                        position: 3,
                        name: 'Talking Avatar',
                        item: 'https://easy-peasy.ai/ai-video-generator/talking-avatar',
                    },
                ],
            },
        ],
    };
    const displayedActors = ACTORS.slice(0, 18);
    const [avatarStyle, setAvatarStyle] = useState<'real' | 'cartoon' | 'clay'>('real');
    const [isMuted, setIsMuted] = useState(true);
    const avatarVideoRef = useRef<HTMLVideoElement>(null);
    const toggleSound = () => {
        if (avatarVideoRef.current) {
            avatarVideoRef.current.muted = !avatarVideoRef.current.muted;
            setIsMuted(avatarVideoRef.current.muted);
        }
    };
    const AVATAR_STYLES = [
        { key: 'real' as const, label: 'Real', emoji: '🧑' },
        { key: 'cartoon' as const, label: 'Cartoon', emoji: '🎨' },
        { key: 'clay' as const, label: 'Clay', emoji: '🏺' },
    ];
    const avatarVideoUrls: Record<typeof avatarStyle, string> = {
        real: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_final_b5ef5f5c-cd31-42ef-864b-ddbc3a867a84.mp4',
        cartoon: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_captioned_1a22a2fa-5475-453e-9607-06a0ee3486f9.mp4',
        clay: 'https://videos.easy-peasy.ai/27feb2bb-aeb4-4a83-9fb6-8f3f2a15885e/talking-video_final_9e2aa140-7e86-4a47-a1fb-044b10c3b131.mp4',
    };
    const avatarVideoUrl = avatarVideoUrls[avatarStyle];
    return translateReactTree(<>
      <Seo templateTitle={t('AI Talking Avatar Generator | Create Your Digital Presenter')} description={t('Create AI talking avatars in minutes. Choose from 60+ pre-built avatars or upload your own photo. 40+ languages, realistic lip-sync, no camera needed. Perfect for content creation at scale.')} image='https://easy-peasy.ai/api/og-template?title=AI%20Talking%20Avatar%20Generator'/>
      <Head>
        <script type='application/ld+json' dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}/>
      </Head>

      <div className='relative min-h-screen overflow-hidden' style={{ backgroundColor: '#0d0a17' }}>
        <LandingPageNavigation variant='light'/>

        {/* ===================== HERO ===================== */}
        <section className='relative overflow-hidden pb-20 pt-32 sm:pt-40'>
          {/* Noise grain */}
          <div className='pointer-events-none absolute inset-0 z-0 opacity-[0.03]' style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E")`,
            backgroundRepeat: 'repeat',
            backgroundSize: '128px 128px',
        }}/>
          {/* Radial glow */}
          <div className='pointer-events-none absolute left-1/2 top-0 z-0 h-[800px] w-[800px] -translate-x-1/2 -translate-y-1/3 rounded-full opacity-30' style={{
            background: 'radial-gradient(circle, rgba(167,139,250,0.35) 0%, rgba(129,140,248,0.15) 40%, transparent 70%)',
        }}/>
          {/* Edge accent left */}
          <div className='pointer-events-none absolute -left-32 top-1/4 z-0 h-64 w-64 rounded-full opacity-20 blur-3xl' style={{ background: 'rgba(167,139,250,0.5)' }}/>
          {/* Edge accent right */}
          <div className='pointer-events-none absolute -right-32 top-1/3 z-0 h-64 w-64 rounded-full opacity-20 blur-3xl' style={{ background: 'rgba(192,132,252,0.5)' }}/>

          <div className='relative z-10 mx-auto max-w-5xl px-6 text-center'>
            {/* Badge */}
            <div className='mb-8 inline-flex items-center gap-2 rounded-full border border-white/[0.08] bg-white/[0.04] px-4 py-1.5 text-xs font-medium text-white/70 backdrop-blur-sm'>
              <span className='inline-block h-1.5 w-1.5 animate-pulse rounded-full' style={{ backgroundColor: '#a78bfa' }}/>
              Next-Gen AI Avatar Technology
            </div>

            <h1 className='mb-6 text-5xl font-extrabold leading-[1.1] tracking-tight sm:text-7xl'>
              <span className='bg-clip-text text-transparent' style={{
            backgroundImage: 'linear-gradient(135deg, #a78bfa 0%, #c084fc 50%, #818cf8 100%)',
        }}>
                AI Talking
              </span>
              <br />
              <span className='bg-clip-text text-transparent' style={{
            backgroundImage: 'linear-gradient(135deg, #a78bfa 0%, #c084fc 50%, #818cf8 100%)',
        }}>
                Avatar
              </span>
            </h1>

            <p className='mx-auto mb-10 max-w-2xl text-lg leading-relaxed text-white/60 sm:text-xl'>
              Create lifelike talking avatar videos without cameras, studios, or
              makeup. Choose from 60+ avatars or upload your own photo — and let
              AI handle the rest.
            </p>

            {/* Stats row */}
            <div className='mx-auto mb-10 grid max-w-2xl grid-cols-2 gap-4 sm:grid-cols-4'>
              {[
            { value: '60+', label: 'Avatars' },
            { value: '40+', label: 'Languages' },
            { value: '2min', label: 'Max' },
            { value: 'Minutes', label: 'to Create' },
        ].map((stat) => (<div key={stat.label} className='rounded-xl border border-white/[0.06] bg-white/[0.03] px-4 py-3 backdrop-blur-sm'>
                  <div className='text-2xl font-bold' style={{ color: '#a78bfa' }}>
                    {stat.value}
                  </div>
                  <div className='text-xs text-white/50'>{stat.label}</div>
                </div>))}
            </div>

            {/* CTAs */}
            <div className='flex flex-col items-center justify-center gap-4 sm:flex-row'>
              <ButtonLink href='/ai-talking-videos' className='rounded-xl px-8 py-3 text-base font-semibold text-white shadow-lg transition-all hover:scale-[1.03]' style={{
            background: 'linear-gradient(135deg, #a78bfa 0%, #818cf8 100%)',
        }}>
                Create Your Avatar
              </ButtonLink>
              <a href='#avatars' className='text-sm font-medium text-white/60 transition-colors hover:text-white/90'>
                Explore avatars &darr;
              </a>
            </div>
          </div>
        </section>

        {/* ===================== LOGOS ===================== */}
        <section className='relative z-10'>
          <LogosCloud />
        </section>

        {/* ===================== DIGITAL IDENTITY ===================== */}
        <section className='relative z-10 py-24'>
          <div className='mx-auto max-w-6xl px-6'>
            <div className='grid gap-12 lg:grid-cols-2 lg:gap-16'>
              {/* Left column */}
              <div className='flex flex-col justify-center'>
                <SectionLabel>Digital Identity</SectionLabel>
                <h2 className='mb-6 text-3xl font-bold text-white sm:text-4xl'>
                  Create Your Consistent Digital Persona
                </h2>
                <p className='mb-8 text-base leading-relaxed text-white/60'>
                  AI avatars let you build a consistent digital identity that
                  scales with your content needs. Same face, same style, every
                  video. No camera fatigue, no bad hair days, no inconsistent
                  lighting. Your audience sees the same polished presenter
                  whether you publish one video or one hundred.
                </p>

                <ul className='mb-8 space-y-3'>
                  {IDENTITY_BENEFITS.map((benefit) => (<li key={benefit} className='flex items-start gap-3'>
                      <span className='mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full' style={{ backgroundColor: 'rgba(167,139,250,0.15)' }}>
                        <CheckIcon className='h-3 w-3' style={{ color: '#a78bfa' }}/>
                      </span>
                      <span className='text-sm text-white/70'>{benefit}</span>
                    </li>))}
                </ul>

                <div>
                  <ButtonLink href='/ai-talking-videos' className='rounded-xl px-6 py-2.5 text-sm font-semibold text-white transition-all hover:scale-[1.03]' style={{
            background: 'linear-gradient(135deg, #a78bfa 0%, #818cf8 100%)',
        }}>
                    Build Your Avatar
                  </ButtonLink>
                </div>
              </div>

              {/* Right column — avatar style switcher + video */}
              <div className='flex items-center justify-center'>
                <div className='w-full max-w-sm overflow-hidden rounded-2xl border' style={{
            borderColor: 'rgba(167,139,250,0.2)',
            backgroundColor: 'rgba(255,255,255,0.02)',
        }}>
                  {/* Style Switcher */}
                  <div className='flex items-center justify-between border-b px-5 py-4' style={{ borderColor: 'rgba(167,139,250,0.12)' }}>
                    <h3 className='text-sm font-semibold text-white'>
                      Your Avatar
                    </h3>
                    <div className='flex gap-1.5'>
                      {AVATAR_STYLES.map((style) => (<button key={style.key} onClick={() => setAvatarStyle(style.key)} className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition-all duration-200 ${avatarStyle === style.key
                ? 'text-white shadow-lg'
                : 'text-white/50 hover:text-white/80'}`} style={avatarStyle === style.key
                ? {
                    background: 'linear-gradient(135deg, #a78bfa 0%, #818cf8 100%)',
                }
                : { backgroundColor: 'rgba(255,255,255,0.05)' }}>
                          <span>{style.emoji}</span>
                          {style.label}
                        </button>))}
                    </div>
                  </div>

                  {/* Video */}
                  <div className='relative aspect-[9/16] bg-black'>
                    <video ref={avatarVideoRef} key={avatarStyle} loop playsInline autoPlay muted className='h-full w-full object-cover' src={avatarVideoUrl}>
                      Your browser does not support the video tag.
                    </video>
                    {/* Style label overlay */}
                    <div className='absolute left-3 top-3 rounded-full border border-white/10 bg-black/40 px-3 py-1 text-[11px] font-medium text-white/80 backdrop-blur-sm capitalize'>
                      {avatarStyle} style
                    </div>
                    {/* Sound toggle */}
                    <button onClick={toggleSound} className='absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/50 text-white backdrop-blur-sm transition hover:bg-black/70' aria-label={isMuted ? 'Unmute' : 'Mute'}>
                      {isMuted ? (<svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                          <path strokeLinecap='round' strokeLinejoin='round' d='M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z'/>
                          <path strokeLinecap='round' strokeLinejoin='round' d='M17 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2'/>
                        </svg>) : (<svg className='h-4 w-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                          <path strokeLinecap='round' strokeLinejoin='round' d='M15.536 8.464a5 5 0 010 7.072M18.364 5.636a9 9 0 010 12.728M5.586 15H4a1 1 0 01-1-1v-4a1 1 0 011-1h1.586l4.707-4.707C10.923 3.663 12 4.109 12 5v14c0 .891-1.077 1.337-1.707.707L5.586 15z'/>
                        </svg>)}
                    </button>
                  </div>

                  {/* Pills + tagline */}
                  <div className='px-5 py-4'>
                    <div className='mb-3 flex flex-wrap gap-1.5'>
                      {AVATAR_PILLS.map((pill) => (<span key={pill} className='rounded-full border px-2.5 py-0.5 text-[11px] font-medium text-white/60' style={{
                borderColor: 'rgba(167,139,250,0.2)',
                backgroundColor: 'rgba(167,139,250,0.08)',
            }}>
                          {pill}
                        </span>))}
                    </div>
                    <p className='text-center text-xs text-white/40'>
                      One avatar. Three styles. Unlimited videos.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ===================== COMPARISON ===================== */}
        <section className='relative z-10 py-24'>
          <div className='mx-auto max-w-4xl px-6'>
            <div className='mb-12 text-center'>
              <SectionLabel>Comparison</SectionLabel>
              <h2 className='text-3xl font-bold text-white sm:text-4xl'>
                Avatar vs. Recording Yourself
              </h2>
            </div>

            <div className='overflow-hidden rounded-2xl border' style={{
            borderColor: 'rgba(255,255,255,0.06)',
            backgroundColor: 'rgba(255,255,255,0.02)',
        }}>
              {/* Header row */}
              <div className='grid grid-cols-3 border-b border-white/[0.06]'>
                <div className='px-6 py-4 text-xs font-semibold uppercase tracking-wider text-white/40'>
                  Aspect
                </div>
                <div className='px-6 py-4 text-xs font-semibold uppercase tracking-wider' style={{ color: '#a78bfa' }}>
                  AI Avatar
                </div>
                <div className='px-6 py-4 text-xs font-semibold uppercase tracking-wider text-white/40'>
                  Recording Yourself
                </div>
              </div>

              {/* Data rows */}
              {COMPARISON_ROWS.map((row, i) => (<div key={row.aspect} className={`grid grid-cols-3 ${i < COMPARISON_ROWS.length - 1
                ? 'border-b border-white/[0.06]'
                : ''}`}>
                  <div className='px-6 py-4 text-sm font-medium text-white/70'>
                    {row.aspect}
                  </div>
                  <div className='px-6 py-4 text-sm font-semibold' style={{ color: '#a78bfa' }}>
                    {row.avatar}
                  </div>
                  <div className='px-6 py-4 text-sm text-white/50'>
                    {row.self}
                  </div>
                </div>))}
            </div>
          </div>
        </section>

        {/* ===================== FEATURES ===================== */}
        <section className='relative z-10 py-24'>
          <div className='mx-auto max-w-6xl px-6'>
            <div className='mb-14 text-center'>
              <SectionLabel>Features</SectionLabel>
              <h2 className='text-3xl font-bold text-white sm:text-4xl'>
                Everything You Need to Create Avatar Videos
              </h2>
            </div>

            <div className='grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
              {FEATURES.map((feature) => (<div key={feature.title} className='group rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 transition-all duration-300 hover:border-violet-500/20 hover:bg-white/[0.04]'>
                  <div className='mb-4 flex h-10 w-10 items-center justify-center rounded-xl transition-colors' style={{ backgroundColor: 'rgba(167,139,250,0.1)' }}>
                    <CheckIcon className='h-5 w-5' style={{ color: '#a78bfa' }}/>
                  </div>
                  <h3 className='mb-2 text-base font-semibold text-white'>
                    {feature.title}
                  </h3>
                  <p className='text-sm leading-relaxed text-white/50'>
                    {feature.description}
                  </p>
                </div>))}
            </div>
          </div>
        </section>

        {/* ===================== HOW IT WORKS ===================== */}
        <section className='relative z-10 py-24'>
          <div className='mx-auto max-w-5xl px-6'>
            <div className='mb-14 text-center'>
              <SectionLabel>How It Works</SectionLabel>
              <h2 className='text-3xl font-bold text-white sm:text-4xl'>
                Three Steps to Your Talking Avatar
              </h2>
            </div>

            <div className='grid gap-8 md:grid-cols-3'>
              {STEPS.map((step) => (<div key={step.step} className='text-center'>
                  <div className='mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl text-xl font-bold text-white' style={{ backgroundColor: 'rgba(167,139,250,0.12)' }}>
                    <span style={{ color: '#a78bfa' }}>{step.step}</span>
                  </div>
                  <h3 className='mb-3 text-lg font-semibold text-white'>
                    {step.title}
                  </h3>
                  <p className='text-sm leading-relaxed text-white/50'>
                    {step.description}
                  </p>
                </div>))}
            </div>
          </div>
        </section>

        {/* ===================== AI ACTORS GRID ===================== */}
        <section id='avatars' className='relative z-10 py-24'>
          <div className='mx-auto max-w-6xl px-6'>
            <div className='mb-14 text-center'>
              <SectionLabel>Pre-Built Avatars</SectionLabel>
              <h2 className='mb-4 text-3xl font-bold text-white sm:text-4xl'>
                Pre-Built AI Avatars
              </h2>
              <p className='mx-auto max-w-2xl text-base text-white/50'>
                Choose from a diverse lineup of professional AI presenters. Each
                avatar delivers natural lip-sync and expressive gestures in 40+
                languages.
              </p>
            </div>

            <div className='grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6'>
              {displayedActors.map((actor) => (<div key={actor.id} className='group relative overflow-hidden rounded-xl border border-white/[0.06] bg-white/[0.02] transition-all duration-300 hover:border-violet-500/20'>
                  <div className='relative aspect-[3/4] overflow-hidden'>
                    <Image src={actor.photo} alt={actor.name} layout='fill' objectFit='cover' className='transition-transform duration-500 group-hover:scale-105'/>
                    <div className='absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent'/>
                    {/* Category badge on hover */}
                    {actor.category && (<div className='absolute left-2 top-2 rounded-full bg-black/50 px-2 py-0.5 text-[10px] font-medium text-white/70 opacity-0 backdrop-blur-sm transition-opacity group-hover:opacity-100'>
                        {actor.category}
                      </div>)}
                  </div>
                  <div className='absolute bottom-0 left-0 right-0 p-3'>
                    <h3 className='text-sm font-semibold text-white'>
                      {actor.name}
                    </h3>
                    {actor.description && (<p className='mt-0.5 line-clamp-1 text-[11px] text-white/50'>
                        {actor.description}
                      </p>)}
                  </div>
                </div>))}
            </div>

            <div className='mt-10 text-center'>
              <ButtonLink href='/ai-talking-videos' className='rounded-xl px-8 py-3 text-sm font-semibold text-white transition-all hover:scale-[1.03]' style={{
            background: 'linear-gradient(135deg, #a78bfa 0%, #818cf8 100%)',
        }}>
                Start Using Avatars
              </ButtonLink>
            </div>
          </div>
        </section>

        {/* ===================== USE CASES ===================== */}
        <section className='relative z-10 py-24'>
          <div className='mx-auto max-w-6xl px-6'>
            <div className='mb-14 text-center'>
              <SectionLabel>Use Cases</SectionLabel>
              <h2 className='text-3xl font-bold text-white sm:text-4xl'>
                How Teams Use AI Avatars
              </h2>
            </div>

            <div className='grid gap-5 sm:grid-cols-2 lg:grid-cols-3'>
              {USE_CASES.map((useCase) => (<div key={useCase.title} className='rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 transition-all duration-300 hover:border-violet-500/20 hover:bg-white/[0.04]'>
                  <h3 className='mb-3 text-base font-semibold text-white'>
                    {useCase.title}
                  </h3>
                  <p className='text-sm leading-relaxed text-white/50'>
                    {useCase.description}
                  </p>
                </div>))}
            </div>
          </div>
        </section>

        {/* ===================== FAQ ===================== */}
        <section className='relative z-10 py-24'>
          <div className='mx-auto max-w-3xl px-6'>
            <div className='mb-14 text-center'>
              <SectionLabel>FAQ</SectionLabel>
              <h2 className='text-3xl font-bold text-white sm:text-4xl'>
                Frequently Asked Questions
              </h2>
            </div>

            <div className='space-y-3'>
              {FAQS.map((faq) => (<details key={faq.question} className='group rounded-xl border border-white/[0.06] bg-white/[0.02] transition-colors hover:border-violet-500/20'>
                  <summary className='flex cursor-pointer items-center justify-between px-6 py-5 text-sm font-semibold text-white [&::-webkit-details-marker]:hidden'>
                    {faq.question}
                    <svg className='ml-4 h-4 w-4 flex-shrink-0 text-white/40 transition-transform group-open:rotate-45' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
                      <path strokeLinecap='round' strokeLinejoin='round' d='M12 4v16m8-8H4'/>
                    </svg>
                  </summary>
                  <div className='px-6 pb-5 text-sm leading-relaxed text-white/50'>
                    {faq.answer}
                  </div>
                </details>))}
            </div>
          </div>
        </section>

        {/* ===================== PRICING CTA ===================== */}
        <section className='relative z-10 py-24'>
          <div className='mx-auto max-w-4xl px-6'>
            <div className='overflow-hidden rounded-3xl p-10 text-center sm:p-16' style={{
            background: 'linear-gradient(135deg, rgba(167,139,250,0.15) 0%, rgba(129,140,248,0.1) 50%, rgba(192,132,252,0.15) 100%)',
            border: '1px solid rgba(167,139,250,0.2)',
        }}>
              <h2 className='mb-4 text-3xl font-bold text-white sm:text-4xl'>
                Create Your AI Avatar Today
              </h2>
              <p className='mx-auto mb-8 max-w-xl text-base text-white/60'>
                Join thousands of creators using AI avatars to produce
                professional video content at scale.
              </p>

              <ul className='mx-auto mb-8 flex max-w-md flex-col gap-3 text-left'>
                {[
            '60+ pre-built avatars included',
            'Custom avatar from your photo',
            '40+ languages & accents',
            'HD 1080p video output',
            'Commercial usage rights',
        ].map((item) => (<li key={item} className='flex items-center gap-3'>
                    <CheckIcon className='h-4 w-4 flex-shrink-0' style={{ color: '#a78bfa' }}/>
                    <span className='text-sm text-white/70'>{item}</span>
                  </li>))}
              </ul>

              <ButtonLink href='/ai-talking-videos' className='rounded-xl px-8 py-3 text-base font-semibold text-white shadow-lg transition-all hover:scale-[1.03]' style={{
            background: 'linear-gradient(135deg, #a78bfa 0%, #818cf8 100%)',
        }}>
                Get Started Free
              </ButtonLink>
            </div>
          </div>
        </section>

        {/* ===================== RELATED TOOLS ===================== */}
        <section className='relative z-10 py-24'>
          <div className='mx-auto max-w-5xl px-6'>
            <div className='mb-12 text-center'>
              <SectionLabel>Related Tools</SectionLabel>
              <h2 className='text-3xl font-bold text-white sm:text-4xl'>
                Related AI Video Tools
              </h2>
            </div>

            <div className='grid gap-5 sm:grid-cols-3'>
              {RELATED_TOOLS.map((tool) => (<Link key={tool.href} href={tool.href} className='group block rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 transition-all duration-300 hover:border-violet-500/20 hover:bg-white/[0.04]'>
                  <h3 className='mb-2 text-base font-semibold text-white transition-colors group-hover:text-violet-300'>
                    {tool.title}
                  </h3>
                  <p className='text-sm leading-relaxed text-white/50'>
                    {tool.description}
                  </p>
                  <span className='mt-4 inline-block text-xs font-medium' style={{ color: '#a78bfa' }}>
                    Learn more &rarr;
                  </span>
                </Link>))}
            </div>
          </div>
        </section>
      </div>

      {/* ===================== SHARED COMPONENTS (white bg) ===================== */}
      <div className='bg-white dark:bg-slate-900'>
        <FeaturedLogos />
        <Testimonials />
      </div>

      <div className='bg-white dark:bg-slate-900'>
        <CallToAction />
        <Footer />
      </div>
    </>, t);
}
const getStaticProps: GetStaticProps = async () => {
    return {
        props: {},
    };
};

    async function __Next_Translate__getStaticProps__1a1052e8f46__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getStaticProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-video-generator/talking-avatar',
            loaderName: 'getStaticProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getStaticProps__1a1052e8f46__ as getStaticProps }
  